/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {AngularModalService} from '../modal/angular-modal.service';

import {wireModelCommsModelActions} from '../controller/model-comms-actions';
import {deferMarkForCheck} from '../util/defer-mark-for-check';
import {handleCopyModalResult} from '../util/handle-copy-modal-result';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-admin-resource-context-actions',
  template: `
    <adn-admin-resource-delete
      [model]="model"
      [modelComms]="modelComms"
      [blockDelete]="blockDelete"
      [confirmation]="confirmation"
      [isNew]="effectiveIsNew"
      [readOnly]="readOnly">
    </adn-admin-resource-delete>
    <a *ngIf="copyConfig && !readOnly"
       class="btn btn-default btn-xs"
       style="margin-left: 5px"
       [class.disabled]="blockDelete"
       [attr.disabled]="blockDelete ? 'disabled' : null"
       (click)="copy()">
      <span class="fa fa-fw fa-copy"></span>{{ 'label.copy' | ogTranslate }}
    </a>
  `
})
export class AdminResourceContextActionsComponent implements OnChanges, OnDestroy {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;
  @Input() readOnly: boolean;
  @Input() blockDelete: any;
  @Input() confirmation: any;
  @Input() copyConfig: any;

  /**
   * Route isNew never flips after save; modelComms does. Bind templates to this
   * so a sync subscriber callback cannot mutate the checked @Input mid-CD (NG0100).
   */
  effectiveIsNew = false;

  private subscribedModelComms: any;
  private destroyed = false;

  constructor(
    private readonly $state: StateService,
    private toastr: ToastrService,
    private angularModalService: AngularModalService,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (this.model && this.modelComms) {
      wireModelCommsModelActions(this.modelComms);
    }
    this.ensureModelCommsSubscription();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  copy(): void {
    if (!this.copyConfig || this.readOnly || this.blockDelete) {
      return;
    }

    if (!this.copyConfig.modalComponent) {
      return;
    }

    const modalRef = this.angularModalService.show(this.copyConfig.modalComponent, {
      initialState: {model: this.model}
    });
    (modalRef.content as any).onCopied.subscribe((result: any) => {
      handleCopyModalResult(() => {
        if (this.destroyed) {
          return;
        }
        if (result.destination) {
          this.$state.go(this.copyConfig.stateUrl, {id: result.model.id}, {inherit: false});
        } else {
          this.toastr.success(
            'A copy of this ' + this.copyConfig.lcase + ' was successfully created.',
            this.copyConfig.ucase + ' Copied'
          );
        }
      });
    });
  }

  private ensureModelCommsSubscription(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }

    this.subscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any, isNew: boolean) => {
      if (this.destroyed || type) {
        return;
      }

      // addSubscriber / modelUpdated can run synchronously during the parent's
      // CD (including the immediate replay). Defer so [isNew] does not flip
      // true→false after it was checked (NG0100).
      deferMarkForCheck(this.cdr, () => {
        if (this.destroyed) {
          return;
        }
        // Never replace the route resource with a plain clone — Activate/Archive need $activate etc.
        if (this.model && data && this.model !== data) {
          if (data.name !== undefined) {
            this.model.name = data.name;
          }
          if (data.fileName !== undefined) {
            this.model.fileName = data.fileName;
          }
          if (data.objectState !== undefined) {
            this.model.objectState = data.objectState;
          }
          if (data.validationWarnings !== undefined) {
            this.model.validationWarnings = data.validationWarnings;
          }
          if (data.status !== undefined) {
            this.model.status = data.status;
          }
        }

        if (typeof isNew === 'boolean') {
          this.effectiveIsNew = isNew;
        }
      });
    });
  }
}
